import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Ruler, Send, Trash2, Plus, Package, FileText, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Comet lane dimensions (mm) - Matching Comet Configurator backend values
const COMET_STANDARD_LANE_WIDTH = 812; // Standard lane passage width
const COMET_DDA_LANE_WIDTH = 1052; // DDA lane passage width  
const COMET_UPRIGHT_WIDTH = 115; // Each upright/cabinet width

// Balustrade dimensions (mm)
const BALUSTRADE_WIDTH = 155; // 75mm each side + 5mm glass

// Comet product codes - widths calculated as lane_width + upright_width
const COMET_PRODUCTS = {
  master: { sku: 'TS-COMET-S1000', name: 'Comet S1000 (Master)', width: 927 },  // 812 + 115
  slave: { sku: 'TS-COMET-S1200', name: 'Comet S1200 (Slave)', width: 927 },    // 812 + 115
  masterDDA: { sku: 'TS-COMET-S1000', name: 'Comet S1000 DDA (Master)', width: 1167 },  // 1052 + 115
  slaveDDA: { sku: 'TS-COMET-S1200', name: 'Comet S1200 DDA (Slave)', width: 1167 },    // 1052 + 115
  // Bases
  singleStandard: { sku: 'ACC-BASE-COMET-STD-TIS-UK', name: 'Single Lane Standard Base' },
  singleDDA: { sku: 'ACC-BASE-COMET-A-UK', name: 'Single Lane DDA Base' },
  standardBase: { sku: 'ACC-BASE-COMET-B-UK', name: 'Standard Lane Base' },
  ddaBase: { sku: 'ACC-BASE-COMET-C-UK', name: 'DDA Lane Base' },
  midpointBase: { sku: 'ACC-BASE-COMET-D-UK', name: 'Bank Midpoint Base' },
};

// Half-Height Products with full SKU codes and linked accessories
const HALF_HEIGHT_PRODUCTS = [
  { 
    id: 'ts1000', 
    name: 'TS1000 Pro', 
    sku: 'TS-TS1000-Pro',
    category: 'half_height',
    width: 810,
    description: 'Tripod Turnstile - Compact',
    color: '#10B981',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/wo5ptzys_TS1000Pro.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/entkewdy_TS1000%20pro-Leaflet-202112%20%281%29.pdf',
    // Linked base (optional)
    linkedBase: { sku: 'TS1000-Base', name: 'TS1000 Pro Base' },
    requiredWings: null // No wings required
  },
  { 
    id: 'ts2000', 
    name: 'TS2000 Pro', 
    sku: 'TS-TS2000-Pro',
    category: 'half_height',
    width: 1110,
    description: 'Tripod Turnstile - Bridge Style',
    color: '#10B981',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/v1rhgjoc_TS2000Pro.png',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/dgm1dcfv_TS2000%20pro-Leaflet-202112%20%281%29.pdf',
    // Linked base (optional)
    linkedBase: { sku: 'TS2000-Base', name: 'TS2000 Pro Base' },
    requiredWings: null
  },
  // Swing Barrier Gates - wings are REQUIRED
  { 
    id: 'sbt3000s', 
    name: 'SBT3000S (Single)', 
    sku: 'TS-SBT3000S-SINGLE-OB-ZK',
    category: 'swing_barrier',
    width: 1040,
    description: 'Single Swing Barrier',
    color: '#8B5CF6',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/rx0wmog6_SBT3000S.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf',
    // Linked base (optional)
    linkedBase: { sku: 'SBT3000S-Base', name: 'SBT3000S Base' },
    // Required wings (mandatory)
    requiredWings: { sku: 'SBT3000-800ACR', name: 'SBT3000 Single Acrylic Infil Wings', qty: 1 }
  },
  { 
    id: 'sbt3000d', 
    name: 'SBT3000D (Double)', 
    sku: 'TS-SBT3000S-DOUBLE-OB-ZK',
    category: 'swing_barrier',
    width: 1610,
    description: 'Double Swing Barrier',
    color: '#8B5CF6',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/ta7oqknd_SBT3000D.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf',
    // Linked base (optional)
    linkedBase: { sku: 'SBT3000D-Base', name: 'SBT3000D Base' },
    // Required wings (mandatory) - 2x for double
    requiredWings: { sku: 'SBT3000-600ACR', name: 'SBT3000 Double Acrylic Infil Wings', qty: 2 }
  },
];

const OTHER_CATEGORIES = [
  { id: 'half_height', name: 'Tripod Turnstile', color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'swing_barrier', name: 'Swing Barrier', color: 'bg-purple-100 text-purple-700 border-purple-300' },
];

const SpaceConfigurator = ({ onBack }) => {
  const [projectName, setProjectName] = useState("");
  const [totalWidth, setTotalWidth] = useState("");
  
  // Comet Bank configuration
  const [cometBank, setCometBank] = useState({
    enabled: false,
    totalLanes: "",
    ddaLanes: "",
    requireBases: "yes"
  });
  
  // Half-Height / Swing Barrier configuration
  const [requireHalfHeightBases, setRequireHalfHeightBases] = useState("yes");
  
  // Other products
  const [selectedCategories, setSelectedCategories] = useState(['half_height', 'swing_barrier']);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);
  const [autoFillBalustrade, setAutoFillBalustrade] = useState(false);

  // Calculate Comet bank width
  const calculateCometWidth = () => {
    if (!cometBank.enabled || !cometBank.totalLanes) return 0;
    
    const totalLanes = parseInt(cometBank.totalLanes) || 0;
    const ddaLanes = parseInt(cometBank.ddaLanes) || 0;
    const standardLanes = totalLanes - ddaLanes;
    const totalUprights = totalLanes + 1;
    
    return (standardLanes * COMET_STANDARD_LANE_WIDTH) + 
           (ddaLanes * COMET_DDA_LANE_WIDTH) + 
           (totalUprights * COMET_UPRIGHT_WIDTH);
  };

  // Calculate Comet breakdown
  const getCometBreakdown = () => {
    if (!cometBank.enabled || !cometBank.totalLanes) return null;
    
    const totalLanes = parseInt(cometBank.totalLanes) || 0;
    const ddaLanes = parseInt(cometBank.ddaLanes) || 0;
    const standardLanes = totalLanes - ddaLanes;
    
    // Master/Slave logic: 1 master (S1000), rest are slaves (S1200)
    const masterLanes = 1;
    const slaveLanes = totalLanes > 1 ? totalLanes - 1 : 0;
    
    // Base logic
    let bases = [];
    if (cometBank.requireBases === 'yes') {
      if (totalLanes === 1) {
        // Single lane - special base
        if (ddaLanes === 1) {
          bases.push({ ...COMET_PRODUCTS.singleDDA, qty: 1 });
        } else {
          bases.push({ ...COMET_PRODUCTS.singleStandard, qty: 1 });
        }
      } else {
        // Multi-lane bank
        if (standardLanes > 0) {
          bases.push({ ...COMET_PRODUCTS.standardBase, qty: standardLanes });
        }
        if (ddaLanes > 0) {
          bases.push({ ...COMET_PRODUCTS.ddaBase, qty: ddaLanes });
        }
        bases.push({ ...COMET_PRODUCTS.midpointBase, qty: 1 });
      }
    }
    
    return {
      totalLanes,
      standardLanes,
      ddaLanes,
      masterLanes,
      slaveLanes,
      bases,
      width: calculateCometWidth()
    };
  };

  // Calculate balustrades needed (for non-Comet products only)
  const calculateBalustrades = (products) => {
    let nonCometCount = products.filter(p => p.needsBalustrade).length;
    if (nonCometCount === 0) return 0;
    return nonCometCount + 1;
  };

  // Get linked accessories for selected products
  const getLinkedAccessories = () => {
    const accessories = {
      bases: [],
      wings: []
    };
    
    selectedProducts.forEach(product => {
      // Add required wings (mandatory for SBT3000)
      if (product.requiredWings) {
        accessories.wings.push({
          ...product.requiredWings,
          forProduct: product.name
        });
      }
      
      // Add optional bases if user selected "yes"
      if (requireHalfHeightBases === 'yes' && product.linkedBase) {
        accessories.bases.push({
          ...product.linkedBase,
          forProduct: product.name
        });
      }
    });
    
    return accessories;
  };

  const cometWidth = calculateCometWidth();
  const cometBreakdown = getCometBreakdown();
  const balustradesNeeded = calculateBalustrades(selectedProducts);
  const balustradeWidth = balustradesNeeded * BALUSTRADE_WIDTH;
  const productWidth = selectedProducts.reduce((sum, p) => sum + p.width, 0);
  const usedWidth = cometWidth + productWidth + balustradeWidth;
  const remainingWidth = (parseInt(totalWidth) || 0) - usedWidth;
  const linkedAccessories = getLinkedAccessories();

  // Gap fill balustrade calculation (R50 frame + infill panel logic from Comet configurator)
  const calculateSingleGapFill = (gapMm) => {
    const R50_WIDTH = 50; // 25mm per upright × 2 uprights per R50 frame
    const MAX_INFILL_WIDTH = 1000; // 1 meter max per infill panel
    if (gapMm <= 0) return { r50Count: 0, infillCount: 0, infillWidth: 0, totalWidth: 0 };
    if (gapMm <= R50_WIDTH) return { r50Count: 1, infillCount: 0, infillWidth: 0, totalWidth: gapMm };
    let numInfills = 1;
    let infillWidth = gapMm - R50_WIDTH;
    while (infillWidth > MAX_INFILL_WIDTH) {
      numInfills++;
      infillWidth = Math.round((gapMm - numInfills * R50_WIDTH) / numInfills);
    }
    return {
      r50Count: numInfills,
      infillCount: numInfills,
      infillWidth: Math.round((gapMm - numInfills * R50_WIDTH) / numInfills),
      totalWidth: gapMm
    };
  };

  // Auto-fill gap calculation: if > 10000mm (1000cm), split into two balustrades (one each side)
  const gapFill = (() => {
    if (!autoFillBalustrade || remainingWidth <= 0) return null;
    if (remainingWidth > 10000) {
      const leftGap = Math.ceil(remainingWidth / 2);
      const rightGap = remainingWidth - leftGap;
      return { split: true, left: calculateSingleGapFill(leftGap), right: calculateSingleGapFill(rightGap), totalWidth: remainingWidth };
    }
    return { split: false, single: calculateSingleGapFill(remainingWidth), totalWidth: remainingWidth };
  })();

  const effectiveRemainingWidth = autoFillBalustrade && gapFill ? 0 : remainingWidth;

  // Filter products by category and remaining width
  const availableProducts = HALF_HEIGHT_PRODUCTS.filter(p => {
    const categoryMatch = selectedCategories.includes(p.category);
    const additionalBalustrade = p.needsBalustrade ? BALUSTRADE_WIDTH : 0;
    const fitsInSpace = (p.width + additionalBalustrade) <= remainingWidth;
    return categoryMatch && fitsInSpace;
  });

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAddProduct = (product) => {
    if (product.width <= remainingWidth) {
      setSelectedProducts(prev => [...prev, { ...product, instanceId: Date.now() }]);
    }
  };

  const handleRemoveProduct = (instanceId) => {
    setSelectedProducts(prev => prev.filter(p => p.instanceId !== instanceId));
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
    setCometBank({ enabled: false, totalLanes: "", ddaLanes: "", requireBases: "yes" });
    setRequireHalfHeightBases("yes");
    setAutoFillBalustrade(false);
  };

  const handleSendToZKTeco = async () => {
    setError("");
    setSuccess("");

    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    if (!totalWidth || parseInt(totalWidth) <= 0) {
      setError("Please enter a valid total width");
      return;
    }
    if (!cometBank.enabled && selectedProducts.length === 0) {
      setError("Please configure a Comet bank or select at least one product");
      return;
    }
    
    // Validate Comet bank if enabled
    if (cometBank.enabled) {
      const totalLanes = parseInt(cometBank.totalLanes);
      const ddaLanes = parseInt(cometBank.ddaLanes) || 0;
      
      if (isNaN(totalLanes) || totalLanes < 1) {
        setError("Comet bank: Total lanes must be at least 1");
        return;
      }
      if (ddaLanes > totalLanes) {
        setError("Comet bank: DDA lanes cannot exceed total lanes");
        return;
      }
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await axios.post(`${API}/space-configurator/send-configuration`, {
        project_name: projectName,
        total_width: parseInt(totalWidth),
        comet_bank: cometBank.enabled ? {
          total_lanes: parseInt(cometBank.totalLanes),
          dda_lanes: parseInt(cometBank.ddaLanes) || 0,
          require_bases: cometBank.requireBases === 'yes',
          breakdown: cometBreakdown
        } : null,
        product_width: productWidth,
        comet_width: cometWidth,
        balustrade_count: balustradesNeeded,
        balustrade_width: balustradeWidth,
        used_width: usedWidth,
        remaining_width: effectiveRemainingWidth,
        require_half_height_bases: requireHalfHeightBases === 'yes',
        gap_fill_balustrade: gapFill ? {
          enabled: true,
          split: gapFill.split,
          total_width: gapFill.totalWidth,
          ...(gapFill.split ? {
            left: { r50_count: gapFill.left.r50Count, infill_count: gapFill.left.infillCount, infill_width: gapFill.left.infillWidth, total_width: gapFill.left.totalWidth },
            right: { r50_count: gapFill.right.r50Count, infill_count: gapFill.right.infillCount, infill_width: gapFill.right.infillWidth, total_width: gapFill.right.totalWidth }
          } : {
            single: { r50_count: gapFill.single.r50Count, infill_count: gapFill.single.infillCount, infill_width: gapFill.single.infillWidth, total_width: gapFill.single.totalWidth }
          })
        } : null,
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          sku: p.sku,
          category: p.category,
          width: p.width,
          needsBalustrade: p.needsBalustrade,
          linkedBase: p.linkedBase,
          requiredWings: p.requiredWings
        })),
        linked_accessories: linkedAccessories,
        user_email: user.email,
        user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        user_company: user.company_name || '',
        user_distributor: user.distributor || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess("Configuration sent to ZKTeco for pricing! You will receive a response shortly.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send configuration");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Pedestrian Entrance Control
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
              <Ruler className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Space Configurator</h1>
          <p className="text-slate-600">Plan your entrance control layout based on available space</p>
        </div>

        {/* Messages */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        {/* Project Details */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Project Configuration</CardTitle>
            <CardDescription>Enter your project details and available space</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  placeholder="e.g., Office Reception Entry"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-width">Total Available Width (mm) *</Label>
                <Input
                  id="total-width"
                  type="number"
                  placeholder="e.g., 5000"
                  value={totalWidth}
                  onChange={(e) => setTotalWidth(e.target.value)}
                  data-testid="total-width-input"
                />
              </div>
            </div>
            
            {/* Width Progress Bar */}
            {totalWidth && parseInt(totalWidth) > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">
                    Space Used: {usedWidth}mm 
                    <span className="text-slate-400 ml-1">
                      ({cometWidth > 0 ? `Comet: ${cometWidth}mm` : ''}
                      {cometWidth > 0 && productWidth > 0 ? ' + ' : ''}
                      {productWidth > 0 ? `Products: ${productWidth}mm` : ''}
                      {balustradesNeeded > 0 ? ` + Balustrades: ${balustradeWidth}mm` : ''})
                    </span>
                  </span>
                  <span className={`font-medium ${effectiveRemainingWidth < 0 ? 'text-red-600' : autoFillBalustrade && gapFill ? 'text-teal-600' : 'text-green-600'}`}>
                    Remaining: {effectiveRemainingWidth}mm{autoFillBalustrade && gapFill ? ' (balustrade filled)' : ''}
                  </span>
                </div>
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${effectiveRemainingWidth < 0 ? 'bg-red-500' : autoFillBalustrade && gapFill ? 'bg-teal-500' : 'bg-indigo-500'}`}
                    style={{ width: `${Math.min(((autoFillBalustrade && gapFill ? parseInt(totalWidth) : usedWidth) / parseInt(totalWidth)) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comet Speedlane Bank Configuration */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Comet Speedlane Bank
                </CardTitle>
                <CardDescription>Configure a complete Comet speedlane bank with lanes and bases</CardDescription>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={cometBank.enabled}
                  onCheckedChange={(checked) => setCometBank(prev => ({ ...prev, enabled: checked }))}
                />
                <span className="text-sm font-medium">Include Comet Bank</span>
              </label>
            </div>
          </CardHeader>
          
          {cometBank.enabled && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="total-lanes">Total Lanes *</Label>
                  <Input
                    id="total-lanes"
                    type="number"
                    min="1"
                    placeholder="e.g., 3"
                    value={cometBank.totalLanes}
                    onChange={(e) => setCometBank(prev => ({ ...prev, totalLanes: e.target.value }))}
                    data-testid="comet-total-lanes"
                  />
                  <p className="text-xs text-slate-500">Minimum 1 lane required</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dda-lanes">DDA (Accessible) Lanes</Label>
                  <Input
                    id="dda-lanes"
                    type="number"
                    min="0"
                    placeholder="e.g., 1"
                    value={cometBank.ddaLanes}
                    onChange={(e) => setCometBank(prev => ({ ...prev, ddaLanes: e.target.value }))}
                    data-testid="comet-dda-lanes"
                  />
                  <p className="text-xs text-slate-500">DDA lanes are 900mm wide (vs 550mm standard)</p>
                </div>
                <div className="space-y-2">
                  <Label>Require Bases?</Label>
                  <RadioGroup 
                    value={cometBank.requireBases} 
                    onValueChange={(value) => setCometBank(prev => ({ ...prev, requireBases: value }))}
                    className="flex gap-4 pt-1"
                  >
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      cometBank.requireBases === 'yes' 
                        ? 'bg-blue-100 border-blue-400 text-blue-700' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}>
                      <RadioGroupItem value="yes" className="sr-only" />
                      <span className="font-medium text-sm">Yes</span>
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      cometBank.requireBases === 'no' 
                        ? 'bg-blue-100 border-blue-400 text-blue-700' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}>
                      <RadioGroupItem value="no" className="sr-only" />
                      <span className="font-medium text-sm">No</span>
                    </label>
                  </RadioGroup>
                  <p className="text-xs text-slate-500">Bases allow cable routing without floor modifications</p>
                </div>
              </div>
              
              {/* Comet Breakdown */}
              {cometBreakdown && cometBreakdown.totalLanes > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Comet Bank Breakdown
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600">Master Lane (S1000)</p>
                      <p className="font-semibold">{cometBreakdown.masterLanes}x</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Slave Lanes (S1200)</p>
                      <p className="font-semibold">{cometBreakdown.slaveLanes}x</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Standard / DDA</p>
                      <p className="font-semibold">{cometBreakdown.standardLanes} / {cometBreakdown.ddaLanes}</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Total Width</p>
                      <p className="font-semibold">{cometBreakdown.width}mm</p>
                    </div>
                  </div>
                  
                  {cometBank.requireBases === 'yes' && cometBreakdown.bases.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-blue-600 text-sm mb-1">Bases Required:</p>
                      <div className="flex flex-wrap gap-2">
                        {cometBreakdown.bases.map((base, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            {base.qty}x {base.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Other Products Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Product Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Additional Products
              </CardTitle>
              <CardDescription>Add tripod turnstiles or swing barriers</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Category Filter */}
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Filter by Category</Label>
                <div className="flex flex-wrap gap-2">
                  {OTHER_CATEGORIES.map(cat => (
                    <label 
                      key={cat.id}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all ${
                        selectedCategories.includes(cat.id) 
                          ? cat.color 
                          : 'bg-white text-slate-400 border-slate-200'
                      }`}
                    >
                      <Checkbox
                        checked={selectedCategories.includes(cat.id)}
                        onCheckedChange={() => handleCategoryToggle(cat.id)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm font-medium">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Do you need bases? */}
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-green-800">Do you need bases?</Label>
                    <p className="text-xs text-green-600 mt-0.5">Bases allow cable routing without floor modifications</p>
                  </div>
                  <div className="flex gap-2">
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all ${
                      requireHalfHeightBases === 'yes' 
                        ? 'bg-green-100 border-green-400 text-green-700' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}>
                      <input
                        type="radio"
                        name="half-height-bases"
                        value="yes"
                        checked={requireHalfHeightBases === 'yes'}
                        onChange={() => setRequireHalfHeightBases('yes')}
                        className="sr-only"
                      />
                      <span className="font-medium text-sm">Yes</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all ${
                      requireHalfHeightBases === 'no' 
                        ? 'bg-green-100 border-green-400 text-green-700' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}>
                      <input
                        type="radio"
                        name="half-height-bases"
                        value="no"
                        checked={requireHalfHeightBases === 'no'}
                        onChange={() => setRequireHalfHeightBases('no')}
                        className="sr-only"
                      />
                      <span className="font-medium text-sm">No</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Note about SBT3000 Wings */}
              {selectedCategories.includes('swing_barrier') && (
                <div className="mb-4 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-700">
                    <Info className="h-3 w-3 inline mr-1" />
                    <strong>Note:</strong> SBT3000 swing barriers require acrylic wings (automatically included).
                  </p>
                </div>
              )}

              {/* Available Products */}
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {!totalWidth || parseInt(totalWidth) <= 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Enter a total width to see available products
                  </p>
                ) : availableProducts.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {remainingWidth <= 0 
                      ? "No more space available" 
                      : "No products fit in the remaining space"}
                  </p>
                ) : (
                  availableProducts.map(product => (
                    <div 
                      key={product.id}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-indigo-300 transition-colors"
                      data-testid={`product-${product.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded bg-slate-100 flex items-center justify-center overflow-hidden border">
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div 
                              className="w-full h-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ backgroundColor: product.color }}
                            >
                              {product.width}mm
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-slate-500">{product.width}mm wide</p>
                          {product.datasheet && (
                            <a 
                              href={product.datasheet}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <FileText className="h-3 w-3" />
                              Datasheet
                            </a>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddProduct(product)}
                        className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Visual Preview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ruler className="h-5 w-5" />
                Visual Preview
              </CardTitle>
              <CardDescription>See how your selection looks (to scale)</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Visual Area */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 min-h-[200px]">
                {!cometBank.enabled && selectedProducts.length === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-slate-400">
                    <p>Configure a Comet bank or select products to see preview</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Visual representation */}
                    <div className="relative h-32 bg-slate-200 rounded-lg overflow-hidden flex">
                      {/* Left balustrade fill (split mode only) */}
                      {autoFillBalustrade && gapFill?.split && (
                        <div
                          className="h-full bg-teal-500 flex items-center justify-center text-white text-[10px] font-medium border-r-2 border-white"
                          style={{ 
                            width: totalWidth ? `${(gapFill.left.totalWidth / parseInt(totalWidth)) * 100}%` : 'auto',
                            minWidth: '50px'
                          }}
                          title={`Left Balustrade Fill (${gapFill.left.totalWidth}mm) - ${gapFill.left.r50Count}x R50 + ${gapFill.left.infillCount}x ${gapFill.left.infillWidth}mm Infill`}
                        >
                          <div className="text-center px-1">
                            <span className="block text-[9px]">Balustrade</span>
                            <span className="block text-[8px] opacity-80">{gapFill.left.totalWidth}mm</span>
                          </div>
                        </div>
                      )}

                      {/* Comet Bank */}
                      {cometBank.enabled && cometBreakdown && cometBreakdown.width > 0 && (
                        <div
                          className="h-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium border-r-2 border-white"
                          style={{ 
                            width: totalWidth ? `${(cometBreakdown.width / parseInt(totalWidth)) * 100}%` : 'auto',
                            minWidth: '80px'
                          }}
                          title={`Comet Bank (${cometBreakdown.width}mm)`}
                        >
                          <div className="text-center px-2">
                            <span className="block">Comet</span>
                            <span className="text-[10px] opacity-80">{cometBreakdown.totalLanes} lanes</span>
                            <span className="block text-[9px] opacity-70">{cometBreakdown.width}mm</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Other Products with balustrades */}
                      {selectedProducts.map((product, index) => {
                        const showBalustradeBefore = product.needsBalustrade && (index === 0 || selectedProducts[index - 1]?.needsBalustrade);
                        const showBalustradeAfter = product.needsBalustrade && index === selectedProducts.length - 1;
                        
                        return (
                          <div key={product.instanceId} className="flex h-full">
                            {showBalustradeBefore && (
                              <div
                                className="h-full bg-amber-400 flex items-center justify-center text-amber-900 text-[9px] font-bold"
                                style={{ 
                                  width: totalWidth ? `${(BALUSTRADE_WIDTH / parseInt(totalWidth)) * 100}%` : '8px',
                                  minWidth: '6px'
                                }}
                                title={`Balustrade (${BALUSTRADE_WIDTH}mm)`}
                              >
                                <span className="rotate-90">B</span>
                              </div>
                            )}
                            
                            <div
                              className="h-full flex flex-col items-center justify-center text-white text-xs font-medium border-r border-white/30 relative group"
                              style={{ 
                                backgroundColor: product.color,
                                width: totalWidth ? `${(product.width / parseInt(totalWidth)) * 100}%` : 'auto',
                                minWidth: '40px'
                              }}
                              title={`${product.name} (${product.width}mm)`}
                            >
                              <span className="truncate px-1 text-[10px]">{product.name.split(' ')[0]}</span>
                              <span className="text-[9px] opacity-80">{product.width}mm</span>
                              <button
                                onClick={() => handleRemoveProduct(product.instanceId)}
                                className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </div>
                            
                            {showBalustradeAfter && (
                              <div
                                className="h-full bg-amber-400 flex items-center justify-center text-amber-900 text-[9px] font-bold"
                                style={{ 
                                  width: totalWidth ? `${(BALUSTRADE_WIDTH / parseInt(totalWidth)) * 100}%` : '8px',
                                  minWidth: '6px'
                                }}
                                title={`Balustrade (${BALUSTRADE_WIDTH}mm)`}
                              >
                                <span className="rotate-90">B</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Gap fill balustrade (non-split or right side of split) */}
                      {autoFillBalustrade && gapFill ? (
                        <div
                          className="h-full bg-teal-500 flex items-center justify-center text-white text-[10px] font-medium"
                          style={{ 
                            width: totalWidth ? `${((gapFill.split ? gapFill.right.totalWidth : gapFill.single.totalWidth) / parseInt(totalWidth)) * 100}%` : 'auto',
                            minWidth: '50px'
                          }}
                          title={gapFill.split 
                            ? `Right Balustrade Fill (${gapFill.right.totalWidth}mm) - ${gapFill.right.r50Count}x R50 + ${gapFill.right.infillCount}x ${gapFill.right.infillWidth}mm Infill`
                            : `Balustrade Fill (${gapFill.single.totalWidth}mm) - ${gapFill.single.r50Count}x R50 + ${gapFill.single.infillCount}x ${gapFill.single.infillWidth}mm Infill`
                          }
                        >
                          <div className="text-center px-1">
                            <span className="block text-[9px]">{gapFill.split ? 'Balustrade' : 'Balustrade Fill'}</span>
                            <span className="block text-[8px] opacity-80">{gapFill.split ? gapFill.right.totalWidth : gapFill.single.totalWidth}mm</span>
                          </div>
                        </div>
                      ) : (
                        /* Remaining space */
                        remainingWidth > 0 && totalWidth && (
                          <div 
                            className="h-full bg-slate-300 flex items-center justify-center text-slate-500 text-xs"
                            style={{ width: `${(remainingWidth / parseInt(totalWidth)) * 100}%` }}
                          >
                            {remainingWidth}mm
                          </div>
                        )
                      )}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-2">
                      {cometBank.enabled && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 border border-blue-300">
                          Comet Speedlane
                        </span>
                      )}
                      {OTHER_CATEGORIES.filter(cat => selectedProducts.some(p => p.category === cat.id)).map(cat => (
                        <span key={cat.id} className={`text-xs px-2 py-1 rounded ${cat.color}`}>
                          {cat.name}
                        </span>
                      ))}
                      {balustradesNeeded > 0 && (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 border border-amber-300">
                          Balustrade ({balustradesNeeded}x)
                        </span>
                      )}
                      {autoFillBalustrade && gapFill && (
                        <span className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-700 border border-teal-300">
                          Gap Fill (R50 + Infill)
                        </span>
                      )}
                    </div>

                    {/* Auto-fill balustrade option */}
                    {remainingWidth > 0 && (
                      <div className={`p-3 rounded-lg border-2 transition-all ${autoFillBalustrade ? 'bg-teal-50 border-teal-300' : 'bg-slate-50 border-slate-200'}`}>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="auto-fill-balustrade-toggle">
                          <Checkbox
                            checked={autoFillBalustrade}
                            onCheckedChange={(checked) => setAutoFillBalustrade(checked)}
                            className="mt-0.5"
                          />
                          <div>
                            <span className="font-medium text-sm">Fill remaining space with balustrade</span>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Auto-add R50 frames and infill panels to fill the {remainingWidth}mm gap
                              {remainingWidth > 10000 && ' (split into two sides as gap exceeds 10m)'}
                            </p>
                            {autoFillBalustrade && gapFill && (
                              <div className="mt-2 text-xs space-y-1">
                                {gapFill.split ? (
                                  <>
                                    <p className="text-teal-700 font-medium">Two-sided fill ({gapFill.totalWidth}mm total):</p>
                                    <p className="text-teal-600">
                                      Left: {gapFill.left.r50Count}x R50 Frame + {gapFill.left.infillCount}x Infill ({gapFill.left.infillWidth}mm each) = {gapFill.left.totalWidth}mm
                                    </p>
                                    <p className="text-teal-600">
                                      Right: {gapFill.right.r50Count}x R50 Frame + {gapFill.right.infillCount}x Infill ({gapFill.right.infillWidth}mm each) = {gapFill.right.totalWidth}mm
                                    </p>
                                  </>
                                ) : (
                                  <p className="text-teal-700">
                                    {gapFill.single.r50Count}x R50 Frame (ACC-TSA-R50) + {gapFill.single.infillCount}x Infill Panel ({gapFill.single.infillWidth}mm each) = {gapFill.single.totalWidth}mm
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Products Summary */}
              {(cometBank.enabled || selectedProducts.length > 0) && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm font-medium">Configuration Summary</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelection}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto text-sm">
                    {cometBank.enabled && cometBreakdown && (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                        <span className="font-medium text-blue-800">Comet Bank:</span>
                        <span className="text-blue-600 ml-2">
                          {cometBreakdown.masterLanes}x S1000 + {cometBreakdown.slaveLanes}x S1200
                          {cometBreakdown.ddaLanes > 0 && ` (${cometBreakdown.ddaLanes} DDA)`}
                          {cometBank.requireBases === 'yes' && ' + Bases'}
                        </span>
                      </div>
                    )}
                    {selectedProducts.map((product, index) => (
                      <div 
                        key={product.instanceId}
                        className="p-2 bg-white border rounded"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded text-white text-xs flex items-center justify-center" style={{ backgroundColor: product.color }}>
                              {index + 1}
                            </span>
                            <span className="font-medium">{product.name}</span>
                            <span className="text-slate-400">({product.width}mm)</span>
                          </div>
                          <button
                            onClick={() => handleRemoveProduct(product.instanceId)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {/* Show linked accessories */}
                        <div className="ml-7 mt-1 text-xs space-y-0.5">
                          {product.requiredWings && (
                            <p className="text-purple-600">
                              + {product.requiredWings.qty}x {product.requiredWings.name} <span className="text-purple-400">(required)</span>
                            </p>
                          )}
                          {requireHalfHeightBases === 'yes' && product.linkedBase && (
                            <p className="text-green-600">
                              + 1x {product.linkedBase.name} <span className="text-green-400">(base)</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Accessories Summary */}
                    {(linkedAccessories.wings.length > 0 || linkedAccessories.bases.length > 0) && (
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded mt-2">
                        <p className="font-medium text-slate-700 text-xs mb-1">Linked Accessories:</p>
                        {linkedAccessories.wings.length > 0 && (
                          <div className="text-xs text-purple-600">
                            {linkedAccessories.wings.map((w, i) => (
                              <span key={i} className="mr-2">• {w.qty}x {w.sku}</span>
                            ))}
                          </div>
                        )}
                        {linkedAccessories.bases.length > 0 && (
                          <div className="text-xs text-green-600">
                            {linkedAccessories.bases.map((b, i) => (
                              <span key={i} className="mr-2">• 1x {b.sku}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Gap Fill Balustrade Summary */}
                    {autoFillBalustrade && gapFill && (
                      <div className="p-2 bg-teal-50 border border-teal-200 rounded mt-2" data-testid="gap-fill-summary">
                        <p className="font-medium text-teal-800 text-xs mb-1">
                          Gap Fill Balustrade ({gapFill.totalWidth}mm):
                        </p>
                        {gapFill.split ? (
                          <div className="text-xs text-teal-600 space-y-0.5">
                            <p>Left Side: {gapFill.left.r50Count}x ACC-TSA-R50 + {gapFill.left.infillCount}x Infill ({gapFill.left.infillWidth}mm)</p>
                            <p>Right Side: {gapFill.right.r50Count}x ACC-TSA-R50 + {gapFill.right.infillCount}x Infill ({gapFill.right.infillWidth}mm)</p>
                          </div>
                        ) : (
                          <div className="text-xs text-teal-600">
                            <p>{gapFill.single.r50Count}x ACC-TSA-R50 + {gapFill.single.infillCount}x Infill ({gapFill.single.infillWidth}mm)</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={handleClearSelection} 
            disabled={!cometBank.enabled && selectedProducts.length === 0}
            data-testid="clear-selection-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Selection
          </Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleSendToZKTeco}
            disabled={sending || (!cometBank.enabled && selectedProducts.length === 0)}
            data-testid="send-to-zkteco-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending..." : "Send to ZKTeco for Pricing"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpaceConfigurator;
